import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

function ScoreTable({ rows, translate, includeLastRound = false, size = 'medium', playerStatus = {} }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { minPlayCount, allRoundsEqual } = useMemo(() => {
    const playCounts = rows
      .filter((row) => playerStatus[row.name] !== 'inactive') // Only consider active players
      .map((row) => row.playCount)
      .filter((value) => typeof value === 'number');

    if (!playCounts.length) {
      return { minPlayCount: null, allRoundsEqual: true };
    }

    const minCount = Math.min(...playCounts);
    const allEqual = playCounts.every((count) => count === playCounts[0]);
    return { minPlayCount: minCount, allRoundsEqual: allEqual };
  }, [rows, playerStatus]);

  const columns = [
    { key: 'rank', label: isMobile ? '#' : translate('common.rank'), align: 'left' },
    { key: 'player', label: translate('common.player'), align: 'left' },
    { key: 'score', label: translate('common.score'), align: 'right' },
  ];

  if (includeLastRound) {
    columns.push({ key: 'lastRound', label: isMobile ? '+/-' : translate('tvMode.lastRound'), align: 'right' });
  }

  columns.push(
    { key: 'wdl', label: 'W/D/L', align: 'right' },
    { key: 'played', label: isMobile ? 'P' : translate('tournament.playedMatches'), align: 'right' }
  );

  const cellSxByKey = {
    rank: { width: isMobile ? 28 : 44 },
    player: { width: isMobile ? 'auto' : '34%' },
    score: { width: isMobile ? 56 : 80 },
    lastRound: { width: isMobile ? 56 : 86 },
    wdl: { width: isMobile ? 72 : 96 },
    played: { width: isMobile ? 42 : 72 }
  };

  const scoreColumnSx = {
    backgroundColor: 'action.selected',
    borderLeft: 1,
    borderRight: 1,
    borderColor: 'divider'
  };

  return (
    <Table
      size={isMobile ? 'small' : size}
      sx={{
        width: '100%',
        tableLayout: 'fixed',
        '& .MuiTableCell-root': {
          px: isMobile ? 0.75 : 1.5,
          py: isMobile ? 0.65 : 1,
          fontSize: isMobile ? '0.78rem' : '0.875rem'
        }
      }}
    >
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <TableCell
              key={column.key}
              align={column.align}
              sx={{
                fontWeight: 800,
                ...cellSxByKey[column.key],
                ...(column.key === 'score' ? scoreColumnSx : {})
              }}
            >
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => {
          const isInactive = playerStatus[row.name] === 'inactive';
          const showLeastGamesTag =
            !isInactive &&
            typeof row.playCount === 'number' &&
            minPlayCount !== null &&
            row.playCount === minPlayCount &&
            !allRoundsEqual;

          return (
            <TableRow key={row.name} sx={isInactive ? { opacity: 0.6 } : undefined}>
              <TableCell sx={{ fontWeight: 700, ...cellSxByKey.rank }}>{index + 1}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, minWidth: 0 }}>
                  <Box
                    component="span"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {row.name}
                  </Box>
                  {!isMobile && isInactive && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="default"
                      label={translate('tournament.playerInactive')}
                    />
                  )}
                  {!isMobile && showLeastGamesTag && (
                    <Tooltip title={translate('results.leastGamesTooltip')}>
                      <Chip
                        size="small"
                        variant="outlined"
                        color="warning"
                        label={translate('results.leastGames')}
                      />
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...scoreColumnSx,
                  ...cellSxByKey.score,
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 900
                }}
              >
                {row.score}
              </TableCell>
              {includeLastRound && (
                <TableCell
                  align="right"
                  sx={{
                    ...cellSxByKey.lastRound,
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 700,
                    color: row.lastRoundScore !== null && row.lastRoundScore !== undefined
                      ? 'success.main'
                      : 'text.secondary'
                  }}
                >
                  {row.lastRoundScore !== null && row.lastRoundScore !== undefined ? `+${row.lastRoundScore}` : '—'}
                </TableCell>
              )}
              <TableCell align="right" sx={{ ...cellSxByKey.wdl, fontVariantNumeric: 'tabular-nums' }}>
                {(row.wins ?? 0)}/{(row.draws ?? 0)}/{(row.losses ?? 0)}
              </TableCell>
              <TableCell align="right" sx={{ ...cellSxByKey.played, fontVariantNumeric: 'tabular-nums' }}>
                {typeof row.playCount === 'number' ? row.playCount : '-'}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

ScoreTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      score: PropTypes.number.isRequired,
      wins: PropTypes.number,
      draws: PropTypes.number,
      losses: PropTypes.number,
      playCount: PropTypes.number,
      lastRoundScore: PropTypes.number
    })
  ).isRequired,
  translate: PropTypes.func.isRequired,
  includeLastRound: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  playerStatus: PropTypes.object
};

export default ScoreTable;
